import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { Session } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import type { Employee } from "@/types/db";
import { setLanguage } from "@/i18n";
import { loginCandidates, isCredentialRejection } from "@/lib/auth-email-alias";

interface AuthContextValue {
  session: Session | null;
  employee: Employee | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  refreshEmployee: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: string | null }>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const EMP_CACHE_KEY = "scenta-hq-emp-cache-v1";

// Quick localStorage cache of the employee row. Painting from cache lets the
// dashboard render immediately on PWA cold-launch; the real fetch refreshes
// in the background. Keyed by auth_user_id so log-out / different-user safely
// invalidates.
function readCachedEmployee(authUserId: string): Employee | null {
  try {
    const raw = localStorage.getItem(EMP_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { authUserId: string; emp: Employee };
    if (parsed.authUserId !== authUserId) return null;
    return parsed.emp;
  } catch { return null; }
}
function writeCachedEmployee(authUserId: string, emp: Employee | null) {
  try {
    if (!emp) { localStorage.removeItem(EMP_CACHE_KEY); return; }
    localStorage.setItem(EMP_CACHE_KEY, JSON.stringify({ authUserId, emp }));
  } catch { /* quota or private mode — ignore */ }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(true);

  async function fetchEmployee(authUserId: string) {
    const { data, error } = await supabase
      .from("hq_employees")
      .select("*")
      .eq("auth_user_id", authUserId)
      .maybeSingle();
    if (error) {
      console.warn("fetchEmployee error", error);
    }
    const emp = (data as Employee) ?? null;
    setEmployee(emp);
    writeCachedEmployee(authUserId, emp);
    if (emp?.language_preference) {
      setLanguage(emp.language_preference);
    }
  }

  async function refreshEmployee() {
    if (session?.user?.id) await fetchEmployee(session.user.id);
  }

  useEffect(() => {
    let mounted = true;
    // Safety net: never let the app hang on the "Loading…" splash. If the initial
    // session check stalls (seen on iOS standalone PWAs), stop showing the loader
    // anyway — onAuthStateChange still delivers the session when it lands, and the
    // app re-renders authenticated.
    const failSafe = window.setTimeout(() => { if (mounted) setLoading(false); }, 3000);
    // AUTH-UI-001. `loading` used to be cleared as soon as getSession resolved,
    // while the employee lookup was still in flight — `void fetchEmployee(...)`.
    // ProtectedRoute reads `!employee` as "this account has no employee record"
    // and sends it to /welcome, which asks an existing employee to set a
    // password they already have. Going to "/" a second later worked, because by
    // then the lookup had landed. A first sign-in with nothing in the cache hit
    // it every time.
    //
    // The lookup is awaited now. The cached fast path still paints immediately,
    // so this costs nothing to somebody who has signed in before, and the
    // three-second failsafe still guarantees the splash cannot hang.
    supabase.auth.getSession().then(async ({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      const uid = data.session?.user?.id;
      if (!uid) return;
      // Paint from cache immediately; the lookup below is still authoritative.
      const cached = readCachedEmployee(uid);
      if (cached) setEmployee(cached);
      await fetchEmployee(uid);
    }).catch(err => {
      console.warn("getSession failed", err);
    }).finally(() => {
      if (mounted) { clearTimeout(failSafe); setLoading(false); }
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, sess) => {
      if (!mounted) return;
      setSession(sess);
      const uid = sess?.user?.id;
      if (!uid) { setEmployee(null); writeCachedEmployee("", null); return; }
      // CRITICAL: never call (let alone await) Supabase inside this callback.
      // Supabase runs it while holding the auth client's internal lock; an
      // awaited query there deadlocks the client, after which EVERY subsequent
      // query hangs on "Loading…" until a full page reload. Defer to a macrotask
      // so the callback returns immediately and the client stays unblocked.
      setTimeout(() => { if (mounted) void fetchEmployee(uid); }, 0);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // ── EITHER COMPANY ADDRESS SIGNS YOU IN ──────────────────────────────
  // A Supabase user has exactly one email, and the rebrand gave staff two.
  // Every staff record has since moved to @mysundaystate.com, so what this
  // now catches is somebody typing the address they used for a year. The
  // typed address is tried first and its company counterpart second. See
  // lib/auth-email-alias.ts, including how to remove this once the old
  // addresses are dead.
  async function signIn(email: string, password: string) {
    let firstError: string | null = null;
    for (const candidate of loginCandidates(email)) {
      const { error } = await supabase.auth.signInWithPassword({ email: candidate, password });
      if (!error) return { error: null };
      firstError ??= error.message;
      // Only a refusal is worth a second try. A rate limit or an outage means
      // stop — retrying there compounds it.
      if (!isCredentialRejection(error.message)) return { error: error.message };
    }
    // Report the first failure, which is about the address they actually typed.
    return { error: firstError };
  }

  // HQ-AUTH-REGISTER-001: signUp() is gone. It called supabase.auth.signUp and
  // then hq_bootstrap_employee, which is how an anonymous visitor became an
  // employee of this company. Accounts come from an administrator's invitation;
  // /welcome links an invited person to the record already waiting for them.
  // Leaving a dead sign-up helper here would be leaving the path one import
  // away from working again.

  async function signOut() {
    await supabase.auth.signOut();
    setEmployee(null);
  }

  // Goes through our own function, not supabase.auth.resetPasswordForEmail:
  // Supabase's mailer corrupts the "?token=" in the link it sends, so those reset
  // emails arrived (or appeared not to) with a dead link. See
  // netlify/functions/_auth-mail.js. The endpoint always reports success so it
  // can't be used to probe which emails have accounts.
  async function resetPassword(email: string) {
    try {
      const res = await fetch("/.netlify/functions/request-password-reset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) return { error: "Couldn't send the reset email. Try again in a moment." };
      return { error: null };
    } catch {
      return { error: "Couldn't reach the server. Check your connection and try again." };
    }
  }

  return (
    <AuthContext.Provider value={{ session, employee, loading, signIn, signOut, refreshEmployee, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
